import React, { useMemo, useState } from 'react';
import { Card, Empty } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useNotifications } from '../hooks/useNotifications';
import { TimeRangeLinks } from '../ui/TimeRangeLinks';
import { formatCurrency } from '../utils/numbers';

type Period = 'day' | 'week' | 'month' | 'year' | 'all';
type Props = { height?: number; rangeDays?: number };

const D = 24 * 60 * 60 * 1000;
const toPeriod = (d?: number): Period =>
  d === 1 ? 'day' : d === 7 ? 'week' : d === 30 ? 'month' : d === 365 ? 'year' : 'month';

const PERIOD_LABEL: Record<Period, string> = {
  day: '(24 ч)',
  week: '(7 дн)',
  month: '(30 дн)',
  year: '(365 дн)',
  all: '(за всё время)',
};

const ruMonthsShort = ['Янв','Фев','Мар','Апр','Май','Июн','Июл','Авг','Сен','Окт','Ноя','Дек'];
const weekLabels = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

// Длинная палитра, чтобы хватало на любые случаи
const PALETTE = [
  '#ff4d4f','#faad14','#1677ff','#13c2c2','#ff7a45','#52c41a','#9254de',
  '#2f54eb','#5cdbd3','#a0d911','#fa541c','#eb2f96'
];

const rangeFor = (p: Period) => {
  const now = Date.now();
  switch (p) {
    case 'day':   return { from: now - D,        to: now };
    case 'week':  return { from: now - 7 * D,    to: now };
    case 'month': return { from: now - 30 * D,   to: now };
    case 'year':  return { from: now - 365 * D,  to: now };
    case 'all':   return { from: 0,              to: now };
  }
};

const fmtAMD = (n: number) => formatCurrency(n, 'AMD');
const fmtPct = (f: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(f * 100) + '%';

const RevenueSplitDonut: React.FC<Props> = ({ height = 420, rangeDays }) => {
  const { items } = useNotifications(10_000);
  const [period, setPeriod] = useState<Period>(toPeriod(rangeDays));

  const { rows, total } = useMemo(() => {
    const { from, to } = rangeFor(period);

    // подсуммирование выручки по нужным “корзинам”
    const buckets = new Map<string, number>();

    let srcTotal = 0; // сумма по источнику (для dev-проверки)

    for (const n of items) {
      const t = new Date(n.createdAt).getTime();
      if (!Number.isFinite(t) || t < from || t >= to) continue; // [from, to)

      const dt = new Date(t);
      const val = Number(n.total) || 0;
      if (!val) continue;
      srcTotal += val;

      let key = '';   // метка сегмента
      switch (period) {
        case 'all': {
          key = String(dt.getFullYear());               // годы
          break;
        }
        case 'year': {
          key = ruMonthsShort[dt.getMonth()];           // месяцы
          break;
        }
        case 'month': {
          // 1-я/2-я/3-я/4-я “недели” месяца (по дням: 1-7, 8-14, 15-21, 22-конец)
          const weekIdx = Math.min(3, Math.floor((dt.getDate() - 1) / 7));
          key = `${weekIdx + 1}-я неделя`;
          break;
        }
        case 'week': {
          const wd = (dt.getDay() + 6) % 7;             // 0 = Пн
          key = weekLabels[wd];                          // дни недели
          break;
        }
        case 'day': {
          // Почасовая с 09:00 до 21:00
          const h = dt.getHours();
          if (h < 9 || h > 21) continue;                // вне диапазона — игнорируем
          key = `${String(h).padStart(2,'0')}:00`;
          break;
        }
      }

      buckets.set(key, (buckets.get(key) || 0) + val);
    }

    // Отсортируем ключи человеко-логично
    const orderKeys = () => {
      switch (period) {
        case 'all': {
          return Array.from(buckets.keys()).sort((a,b) => Number(a) - Number(b));
        }
        case 'year': {
          return ruMonthsShort.filter(m => buckets.has(m));
        }
        case 'month': {
          const ks = ['1-я неделя','2-я неделя','3-я неделя','4-я неделя'];
          return ks.filter(k => buckets.has(k));
        }
        case 'week': {
          return weekLabels.filter(w => buckets.has(w));
        }
        case 'day': {
          const ks: string[] = [];
          for (let h=9; h<=21; h++) ks.push(`${String(h).padStart(2,'0')}:00`);
          return ks.filter(k => buckets.has(k));
        }
      }
    };

    const keys = orderKeys();
    const rows = keys.map((k, i) => ({
      name: k,
      value: buckets.get(k) || 0,
      color: PALETTE[i % PALETTE.length],
    }));
    const total = rows.reduce((s, r) => s + r.value, 0);

    if (process.env.NODE_ENV !== 'production' && Math.abs(total - srcTotal) > 0.001) {
      // eslint-disable-next-line no-console
      console.warn('RevenueSplitDonut: сумма сегментов не совпала с суммой по источнику', { total, srcTotal });
    }

    return { rows, total };
  }, [items, period]);


  const Tip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload as { name: string; value: number };
    const pct = total ? fmtPct(p.value / total) : '0%';
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #eee',
          borderRadius: 8,
          padding: '8px 10px',
          boxShadow: '0 4px 16px rgba(0,0,0,.06)',
        }}
      >
        <div style={{ fontWeight: 600 }}>{p.name}</div>
        <div>{fmtAMD(p.value)} · {pct}</div>
      </div>
    );
  };

  return (
    <Card
      variant="borderless"
      className="stat-card stat-card--accent-blue"
      styles={{ header: { paddingTop: 10, paddingBottom: 8, alignItems: 'center' } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="stat-card__icon" style={{ display: 'grid', placeItems: 'center' }} aria-hidden>
            <DollarOutlined />
          </span>
          <span className="stat-card__title">Структура выручки</span>
          <span className="stat-card__suffix">{PERIOD_LABEL[period]}</span>
        </div>
      }
      extra={<TimeRangeLinks value={period} onChange={setPeriod} withAll />}
    >
      <div style={{ width: '100%', height }}>
        {total <= 0 ? (
          <Empty description="Нет данных" />
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Tooltip content={<Tip />} />
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                innerRadius={110}
                outerRadius={170}
                labelLine
                // подпись как в твоём примере: «Янв: 8%», «2-я неделя: 21%», «10:00: 5%» и т.д.
                label={({ name, value }) =>
                  value ? `${name}: ${fmtPct(total ? Number(value) / total : 0)}` : ''
                }
              >
                {rows.map((r, i) => (
                  <Cell key={i} fill={r.color} />
                ))}
              </Pie>

              {/* центр: всего выручки */}
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 13, fill: '#667085' }}
              >
                Всего
              </text>
              <text
                x="50%"
                y="50%"
                dy={18}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 18, fontWeight: 700 }}
              >
                {fmtAMD(total)}
              </text>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default RevenueSplitDonut;
